import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Search, Save, UserRound, UsersRound, Lock, Building2 } from "lucide-react";

const ACCESS_MODES = [
  {
    value: "hospital_shared",
    label: "Hospital Shared",
    icon: Building2,
    desc: "All doctors in this workspace can access this patient.",
  },
  {
    value: "assigned_doctor_only",
    label: "Assigned Doctor Only",
    icon: UserRound,
    desc: "Only the primary doctor and hospital admins can access this patient.",
  },
  {
    value: "care_team",
    label: "Care Team",
    icon: UsersRound,
    desc: "Primary doctor plus selected reliever doctors can access this patient.",
  },
  {
    value: "restricted",
    label: "Restricted",
    icon: Lock,
    desc: "Only explicitly assigned doctors and hospital admins can access this patient.",
  },
];

export default function PatientAccess() {
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, d] = await Promise.all([
        api.get("/patients"),
        api.get("/doctors").catch(() => ({ data: [] })),
      ]);
      setPatients(Array.isArray(p.data) ? p.data : []);
      setDoctors(Array.isArray(d.data) ? d.data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return patients.slice(0, 100);
    return patients
      .filter((p) =>
        [p.full_name, p.patient_number, p.contact_number]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
      .slice(0, 150);
  }, [patients, q]);

  const openPatient = async (p) => {
    setSelected(p);
    setPolicy(null);
    setBusy(true);
    try {
      const { data } = await api.get(`/patients/${p.id}/access-policy`);
      setPolicy({
        access_mode: data.access_mode || "hospital_shared",
        primary_doctor_id: data.primary_doctor_id || "",
        care_team_doctor_ids: data.care_team_doctor_ids || [],
        allow_reliever_access: data.allow_reliever_access !== false,
        emergency_override_enabled: !!data.emergency_override_enabled,
        notes: data.notes || "",
      });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const toggleCareDoctor = (doctorId) => {
    const existing = policy?.care_team_doctor_ids || [];
    const next = existing.includes(doctorId)
      ? existing.filter((x) => x !== doctorId)
      : [...existing, doctorId];

    setPolicy({ ...policy, care_team_doctor_ids: next });
  };

  const save = async () => {
    if (!selected || !policy) return;
    setBusy(true);
    try {
      await api.put(`/patients/${selected.id}/access-policy`, policy);
      toast.success("Patient access policy saved");
      await openPatient(selected);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="patient-access-page">
      <div className="mb-6">
        <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
          Patient Access Control
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Configure which doctors can access shared, reliever, and restricted patients.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Search size={16} />
              Patients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search patient..."
              className="mb-4"
            />

            <div className="space-y-2 max-h-[68vh] overflow-auto pr-1">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => openPatient(p)}
                  className={`w-full text-left rounded-xl border p-3 transition ${
                    selected?.id === p.id
                      ? "border-blue-300 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="font-semibold text-slate-900">{p.full_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {[p.patient_number, p.age ? `${p.age} y/o` : "", p.gender].filter(Boolean).join(" · ") || "No demographics"}
                  </div>
                </button>
              ))}

              {filtered.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">No patients found.</div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck size={17} />
              Access Policy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selected || !policy ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">
                Select a patient to configure access.
              </div>
            ) : (
              <div className="space-y-5">
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xl font-black text-slate-900">{selected.full_name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Patient ID: {selected.patient_number || selected.id}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Access Mode</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {ACCESS_MODES.map((m) => {
                      const Icon = m.icon;
                      const active = policy.access_mode === m.value;
                      return (
                        <button
                          key={m.value}
                          onClick={() => setPolicy({ ...policy, access_mode: m.value })}
                          className={`rounded-2xl border p-4 text-left transition ${
                            active ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon size={17} className={active ? "text-blue-700" : "text-slate-500"} />
                            <div className="font-bold text-slate-900">{m.label}</div>
                          </div>
                          <div className="text-xs text-slate-500 mt-2 leading-relaxed">{m.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Primary Doctor</div>
                  <select
                    className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                    value={policy.primary_doctor_id || ""}
                    onChange={(e) => setPolicy({ ...policy, primary_doctor_id: e.target.value })}
                  >
                    <option value="">No primary doctor</option>
                    {doctors.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name || d.full_name || d.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Care Team / Relievers</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {doctors.map((d) => {
                      const checked = (policy.care_team_doctor_ids || []).includes(d.id);
                      return (
                        <button
                          key={d.id}
                          onClick={() => toggleCareDoctor(d.id)}
                          className={`rounded-xl border p-3 text-left ${
                            checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <div className="font-semibold text-slate-900 text-sm">{d.name || d.full_name || d.email}</div>
                              <div className="text-xs text-slate-500">{d.specialization || d.email || "Doctor"}</div>
                            </div>
                            {checked ? <Badge className="bg-emerald-600">Allowed</Badge> : <Badge variant="outline">No access</Badge>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="rounded-xl border border-slate-200 p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">Allow reliever access</div>
                      <div className="text-xs text-slate-500">Useful for clinics with rotating doctors.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!policy.allow_reliever_access}
                      onChange={(e) => setPolicy({ ...policy, allow_reliever_access: e.target.checked })}
                    />
                  </label>

                  <label className="rounded-xl border border-slate-200 p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">Emergency override</div>
                      <div className="text-xs text-slate-500">Future audited break-glass access.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!policy.emergency_override_enabled}
                      onChange={(e) => setPolicy({ ...policy, emergency_override_enabled: e.target.checked })}
                    />
                  </label>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Admin Notes</div>
                  <textarea
                    value={policy.notes || ""}
                    onChange={(e) => setPolicy({ ...policy, notes: e.target.value })}
                    className="w-full min-h-[90px] rounded-lg border border-slate-200 p-3 text-sm"
                    placeholder="Optional policy notes..."
                  />
                </div>

                <Button onClick={save} disabled={busy} className="w-full h-12 bg-blue-700 hover:bg-blue-800">
                  <Save size={16} className="mr-2" />
                  Save Access Policy
                </Button>

                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                  Phase 1 only stores and audits policies. Backend enforcement should be activated after this admin tool is tested.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
